/**
 * Who may do what to an issue (PRO-3100 TIP-D6–D9, ADR-084 D4–D7). Pure: the API derives
 * `OpsActor.caps` from the caller's role rows (`opsCapabilitiesAt`) and the role lists from
 * `capabilities.ts`, so the domain never duplicates the role map. Every check here is repeated
 * by a 0227 CHECK where one exists (proposer ≠ confirmer, escalator ≠ responder).
 */
import type { PoolClient } from 'pg';
import type {
  OpsCapability,
  OpsConfirmerRule,
  OpsEscalationLevel,
  OpsOrigin,
  OpsOwnerKind,
} from '../types/opsIssues';
import type { OpsDb, OpsIssueRecord } from './opsDb';

export interface OpsActor {
  /** The validated token `oid`. */
  readonly oid: string;
  /** Ops capabilities at the target school, plus caller- and group-class ones (TIP-D5). */
  readonly caps: ReadonlySet<OpsCapability>;
}

/**
 * Roles whose assignment carries each authority, built by the API from `capabilities.ts`.
 * `school` lists are matched on rows at the issue's school; `group` lists on null-school rows.
 */
export interface OpsRoleMap {
  /** TIP-D7 issue owners: roles carrying `ops_issue_read` (school) and `ops_action_own` (group). */
  readonly issueOwner: { readonly school: readonly string[]; readonly group: readonly string[] };
  /** TIP-D7 action owners: the issue-owner roles plus roles carrying only `ops_action_own`. */
  readonly actionOwner: { readonly school: readonly string[]; readonly group: readonly string[] };
  /**
   * TIP-D8: `od` school roles with `ops_issue_confirm_resolution`; COO and CEO group roles.
   * PRO-3140 TIP-D13: `ofstedQa` group roles with `ops_issue_confirm_ofsted`.
   */
  readonly confirmer: {
    readonly od: readonly string[];
    readonly coo: readonly string[];
    readonly ceo: readonly string[];
    readonly ofstedQa: readonly string[];
  };
  /** TIP-D9: OD school roles with `ops_escalation_respond`; COO and CEO group roles. */
  readonly responder: {
    readonly od: readonly string[];
    readonly coo: readonly string[];
    readonly ceo: readonly string[];
  };
}

export function canReadIssue(actor: OpsActor): boolean {
  return (
    actor.caps.has('ops_issue_read') ||
    actor.caps.has('ops_issue_read_group') ||
    actor.caps.has('ops_issue_read_portfolio')
  );
}

/** The Ofsted origins (ADR-084 A2, A3): one set for confirm and read. */
export const OFSTED_ORIGINS: ReadonlySet<OpsOrigin> = new Set<OpsOrigin>([
  'ofsted_readiness',
  'ofsted_inspection',
]);

export function isOfstedOrigin(origin: OpsOrigin): boolean {
  return OFSTED_ORIGINS.has(origin);
}

/**
 * PRO-3140 TIP-D12 (ADR-084 A2): the issue read, or a QA lead reading an Ofsted-origin issue.
 * `ops_issue_confirm_ofsted` admits no other origin and no list.
 */
export function canReadIssueRecord(
  actor: OpsActor,
  issue: { readonly origin: OpsOrigin },
): boolean {
  return (
    canReadIssue(actor) ||
    (actor.caps.has('ops_issue_confirm_ofsted') && isOfstedOrigin(issue.origin))
  );
}

export function isOwner(issue: OpsIssueRecord, actor: OpsActor): boolean {
  return issue.ownerOid === actor.oid;
}

/** Update: `ops_issue_update` at the school, or the current owner with read (MF-2). */
export function canUpdateIssue(issue: OpsIssueRecord, actor: OpsActor): boolean {
  return actor.caps.has('ops_issue_update') || (isOwner(issue, actor) && canReadIssue(actor));
}

/** Propose: `ops_issue_propose_resolution` at the school, or the current owner with read. */
export function canProposeIssue(issue: OpsIssueRecord, actor: OpsActor): boolean {
  return (
    actor.caps.has('ops_issue_propose_resolution') || (isOwner(issue, actor) && canReadIssue(actor))
  );
}

/** Distinct oids of active holders (school rows for `school`, null-school rows for `group`). */
async function holders(
  db: OpsDb,
  c: PoolClient,
  schoolId: string,
  roles: { readonly school: readonly string[]; readonly group: readonly string[] },
): Promise<string[]> {
  const school = await db.listRoleHolders(c, schoolId, roles.school);
  const group = await db.listGroupRoleHolders(c, roles.group);
  return [...new Set([...school, ...group].map((h) => h.oid))];
}

/** TIP-D7: the eligible owners of `kind` at the school, from the role store. */
export async function eligibleOwnerOids(
  db: OpsDb,
  c: PoolClient,
  schoolId: string,
  roles: OpsRoleMap,
  kind: OpsOwnerKind,
): Promise<string[]> {
  return holders(db, c, schoolId, kind === 'issue' ? roles.issueOwner : roles.actionOwner);
}

export interface OpsConfirmerSet {
  readonly rule: OpsConfirmerRule;
  readonly oids: readonly string[];
}

/**
 * TIP-D8: OD holders at the school minus the proposer → `od`; else COO minus the proposer →
 * `coo_fallback`; else CEO minus the proposer → `ceo_fallback`; else none.
 */
export function chooseConfirmers(
  holdersByRung: {
    readonly od: readonly string[];
    readonly coo: readonly string[];
    readonly ceo: readonly string[];
  },
  proposerOid: string,
): OpsConfirmerSet {
  const od = holdersByRung.od.filter((oid) => oid !== proposerOid);
  if (od.length > 0) return { rule: 'od', oids: od };
  const coo = holdersByRung.coo.filter((oid) => oid !== proposerOid);
  if (coo.length > 0) return { rule: 'coo_fallback', oids: coo };
  const ceo = holdersByRung.ceo.filter((oid) => oid !== proposerOid);
  if (ceo.length > 0) return { rule: 'ceo_fallback', oids: ceo };
  return { rule: 'none', oids: [] };
}

/**
 * PRO-3140 TIP-D13 (ADR-084 A2): an Ofsted-origin issue goes to QA-lead holders minus the
 * proposer → `ofsted_qa`; else COO → `coo_fallback`; else CEO → `ceo_fallback`; else none. Never
 * the OD rung.
 */
export function chooseOfstedConfirmers(
  holdersByRung: {
    readonly ofstedQa: readonly string[];
    readonly coo: readonly string[];
    readonly ceo: readonly string[];
  },
  proposerOid: string,
): OpsConfirmerSet {
  const qa = holdersByRung.ofstedQa.filter((oid) => oid !== proposerOid);
  if (qa.length > 0) return { rule: 'ofsted_qa', oids: qa };
  return chooseConfirmers({ od: [], coo: holdersByRung.coo, ceo: holdersByRung.ceo }, proposerOid);
}

/** The confirmer chain for `origin`: Ofsted origin → `chooseOfstedConfirmers`, else TIP-D8. */
export async function resolveConfirmers(
  db: OpsDb,
  c: PoolClient,
  schoolId: string,
  roles: OpsRoleMap,
  proposerOid: string,
  origin: OpsOrigin,
): Promise<OpsConfirmerSet> {
  const group = async (r: readonly string[]) => [
    ...new Set((await db.listGroupRoleHolders(c, r)).map((h) => h.oid)),
  ];
  if (isOfstedOrigin(origin)) {
    return chooseOfstedConfirmers(
      {
        ofstedQa: await group(roles.confirmer.ofstedQa),
        coo: await group(roles.confirmer.coo),
        ceo: await group(roles.confirmer.ceo),
      },
      proposerOid,
    );
  }
  const od = (await db.listRoleHolders(c, schoolId, roles.confirmer.od)).map((h) => h.oid);
  const coo = (await db.listGroupRoleHolders(c, roles.confirmer.coo)).map((h) => h.oid);
  const ceo = (await db.listGroupRoleHolders(c, roles.confirmer.ceo)).map((h) => h.oid);
  return chooseConfirmers(
    { od: [...new Set(od)], coo: [...new Set(coo)], ceo: [...new Set(ceo)] },
    proposerOid,
  );
}

/** The capability the caller must hold for the resolved rule (TIP-D8, PRO-3140 TIP-D13). */
export function holdsConfirmCapability(actor: OpsActor, rule: OpsConfirmerRule): boolean {
  if (rule === 'od') return actor.caps.has('ops_issue_confirm_resolution');
  if (rule === 'coo_fallback' || rule === 'ceo_fallback') {
    return actor.caps.has('ops_issue_confirm_group');
  }
  if (rule === 'ofsted_qa') return actor.caps.has('ops_issue_confirm_ofsted');
  return false;
}

/**
 * TIP-D9: `operations_director` → OD holders at the school; `coo` → COO; `executive` → CEO and
 * COO. Always minus the escalator.
 */
export function chooseResponders(
  holdersByRung: {
    readonly od: readonly string[];
    readonly coo: readonly string[];
    readonly ceo: readonly string[];
  },
  level: OpsEscalationLevel,
  escalatorOid: string,
): string[] {
  const oids =
    level === 'operations_director'
      ? holdersByRung.od
      : level === 'coo'
        ? holdersByRung.coo
        : [...holdersByRung.ceo, ...holdersByRung.coo];
  return [...new Set(oids)].filter((oid) => oid !== escalatorOid);
}

export async function resolveResponders(
  db: OpsDb,
  c: PoolClient,
  schoolId: string,
  roles: OpsRoleMap,
  level: OpsEscalationLevel,
  escalatorOid: string,
): Promise<string[]> {
  const od =
    level === 'operations_director'
      ? (await db.listRoleHolders(c, schoolId, roles.responder.od)).map((h) => h.oid)
      : [];
  const coo =
    level === 'operations_director'
      ? []
      : (await db.listGroupRoleHolders(c, roles.responder.coo)).map((h) => h.oid);
  const ceo =
    level === 'executive'
      ? (await db.listGroupRoleHolders(c, roles.responder.ceo)).map((h) => h.oid)
      : [];
  return chooseResponders({ od, coo, ceo }, level, escalatorOid);
}

/** The capability a responder at `level` must hold. */
export function holdsRespondCapability(actor: OpsActor, level: OpsEscalationLevel): boolean {
  return level === 'operations_director'
    ? actor.caps.has('ops_escalation_respond')
    : actor.caps.has('ops_escalation_respond_group');
}
